import AuthenticationGuard from "./components/pages/authentication-guard";
import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { setAccessTokenGetter, setSessionExpiredHandler } from "src/state/modules/auth-token";
import { Route, Routes } from "react-router-dom";
import CallbackPage from "./components/pages/CallBack";
import NotFoundPage from "./components/pages/NotFound";
import ProfilePage from "./components/pages/Profile";
import { ROUTES } from "./utils/constants/routes";
import { PageLayout as TeamPageLayout } from "./components/pages/Teams/PageLayout";
import EventStartingLineupPage from "./components/pages/Events/StartingLineup";
import EventPlaysPage from "./components/pages/Events/PlaysPage";
import EventStatsPage from "./components/pages/Events/StatsPage";
import EventBoxScorePage from "./components/pages/Events/BoxScore";
import EventVideoPage from "./components/pages/Events/VideoPage";
import EventLivePage from "./components/pages/Events/LivePage";
import TeamsPage from "./components/pages/Teams/TeamsPage";
import PlayerProfilePage from "./components/pages/Teams/Players/PlayerProfilePage";
import TeamPitchSmart from "./components/pages/Teams/PitchSmart";
import { PageLayout as EventPageLayout } from "./components/pages/Events/PageLayout";
import SearchPage from "./components/pages/Search/SearchPage";
import PlayerSearchPage from "./components/pages/PlayerSearch/PlayerSearchPage";
import EventInfoPage from "./components/pages/Events/InfoPage";
import SchedulePage from "./components/pages/Teams/SchedulePage";
import TeamHomePage from "./components/pages/Teams/TeamHomePage";
import HomePageLayout from "./components/pages/HomePage";
import LiveGamesPage from "./components/pages/Live/LiveGamesPage";
import SeasonStatsPage from "./components/pages/Teams/SeasonStatsPage";
import InfoPage from "./components/pages/Teams/InfoPage";
import Users from "./components/pages/Teams/Users";

export default function App() {
  // Expose Auth0's silent token retrieval to the RTK Query base query, which runs outside
  // React and can't call hooks. Registered here because App lives inside <Auth0Provider>.
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  useEffect(() => {
    setAccessTokenGetter(() => getAccessTokenSilently());
    // If the cached session can no longer mint tokens (refresh token expired/revoked), the
    // guarded routes still render but every API call 401s. Force a fresh login and come
    // back to where the user was instead of leaving them on a loaded-but-empty app.
    setSessionExpiredHandler(() => {
      void loginWithRedirect({
        appState: { returnTo: window.location.pathname + window.location.search },
      });
    });
    return () => {
      setAccessTokenGetter(null);
      setSessionExpiredHandler(null);
    };
  }, [getAccessTokenSilently, loginWithRedirect]);

  return (
    <Routes>
      <Route element={<HomePageLayout />}>
        <Route path={ROUTES.home} element={<AuthenticationGuard component={TeamsPage} />} />
        <Route path={ROUTES.teams.list} element={<AuthenticationGuard component={TeamsPage} />} />
        <Route path={ROUTES.import} element={<AuthenticationGuard component={SearchPage} />} />
        <Route path={ROUTES.live} element={<AuthenticationGuard component={LiveGamesPage} />} />
        <Route path={ROUTES.players} element={<AuthenticationGuard component={PlayerSearchPage} />} />
        <Route path={ROUTES.profile} element={<AuthenticationGuard component={ProfilePage} />} />
        <Route path={ROUTES.callback} element={<CallbackPage />} />
        <Route path="*" element={<AuthenticationGuard component={NotFoundPage} />} />
      </Route>
      <Route element={<AuthenticationGuard component={TeamPageLayout} />}>
        <Route path={ROUTES.teams.home} element={<AuthenticationGuard component={TeamHomePage} />} />
        <Route path={ROUTES.teams.events.schedule} element={<AuthenticationGuard component={SchedulePage} />} />
        <Route path={ROUTES.teams.teamInfo} element={<AuthenticationGuard component={InfoPage} />} />
        <Route path={ROUTES.teams.stats} element={<AuthenticationGuard component={SeasonStatsPage} />} />
        <Route path={ROUTES.teams.pitchSmart} element={<AuthenticationGuard component={TeamPitchSmart} />} />
        <Route path={ROUTES.teams.users} element={<AuthenticationGuard component={Users} />} />
        <Route path={ROUTES.teams.playerProfile} element={<AuthenticationGuard component={PlayerProfilePage} />} />
      </Route>
      <Route element={<AuthenticationGuard component={EventPageLayout} />}>
        <Route path={ROUTES.teams.events.scheduleEvent} element={<AuthenticationGuard component={EventInfoPage} />} />
        <Route path={ROUTES.teams.events.boxScore} element={<AuthenticationGuard component={EventBoxScorePage} />} />
        <Route path={ROUTES.teams.events.plays} element={<AuthenticationGuard component={EventPlaysPage} />} />
        <Route path={ROUTES.teams.events.gameStats} element={<AuthenticationGuard component={EventStatsPage} />} />
        <Route path={ROUTES.teams.events.videos} element={<AuthenticationGuard component={EventVideoPage} />} />
        <Route path={ROUTES.teams.events.liveStream} element={<AuthenticationGuard component={EventLivePage} />} />
        <Route
          path={ROUTES.teams.events.startingLineup}
          element={<AuthenticationGuard component={EventStartingLineupPage} />}
        />
      </Route>
    </Routes>
  );
}
